
import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Video, Clock, Calendar, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api/client';

interface RecordingStatus {
  status: 'enabled' | 'disabled' | 'recording' | 'processing' | 'error';
  lastRecording?: string;
  nextRecording?: string;
  recordingCount?: number;
  totalDuration?: number;
}

interface StreamRecordingStatusProps {
  streamId: number;
  compact?: boolean;
}

export function StreamRecordingStatus({ streamId, compact = false }: StreamRecordingStatusProps) {
  const [status, setStatus] = useState<RecordingStatus>({ status: 'disabled' });
  const [isLoading, setIsLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ status: RecordingStatus }>(`api/streams/${streamId}/recording/status`);

      if (response.success && response.data) {
        const recordingStatus = response.data.status;
        setStatus(recordingStatus);
      } else {
        console.error('Fetch recording status error:', response.error);
      }
    } catch (error) {
      console.error('Fetch recording status error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enabled':
      case 'recording':
        return 'bg-green-500';
      case 'disabled':
        return 'bg-gray-400';
      case 'processing':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'enabled':
        return 'Recording Enabled';
      case 'disabled':
        return 'Recording Disabled';
      case 'recording':
        return 'Currently Recording';
      case 'processing':
        return 'Processing Recording';
      case 'error':
        return 'Recording Error';
      default:
        return 'Unknown Status';
    }
  };

  useEffect(() => {
    fetchStatus();
    
  }, [streamId]);

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${getStatusColor(status.status)}`} />
          <span className="text-sm font-medium">{getStatusText(status.status)}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStatus}
          disabled={isLoading}
          className="text-xs"
        >
          <RefreshCw className={`h-3 w-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Video className="h-5 w-5" />
              <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full ${getStatusColor(status.status)}`} />
            </div>
            <div>
              <CardTitle className="text-lg">Recording Status</CardTitle>
              <p className="text-sm text-muted-foreground">Current recording state and information</p>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={fetchStatus} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center gap-3">
          <Badge variant={status.status === 'enabled' || status.status === 'recording' ? 'default' : 'secondary'}>
            {getStatusText(status.status)}
          </Badge>
          {status.status === 'error' && (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
          {status.status === 'enabled' && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
        </div>

        {/* Status Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Current Status</h4>
            <p className="font-medium capitalize">{status.status}</p>
          </div>
          
          {status.recordingCount !== undefined && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Total Recordings</h4>
              <p className="font-medium">{status.recordingCount}</p>
            </div>
          )}
          
          {status.totalDuration && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Total Duration</h4>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>{Math.floor(status.totalDuration / 60)}h {status.totalDuration % 60}m</span>
              </div>
            </div>
          )}
        </div>

        {/* Timeline Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
          {status.lastRecording && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Last Recording</h4>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{new Date(status.lastRecording).toLocaleString()}</span>
              </div>
            </div>
          )}
          
          {status.nextRecording && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Next Recording</h4>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{new Date(status.nextRecording).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}